import {
  Timestamp, collection, doc, getDoc, getDocs, limit, orderBy, query,
  serverTimestamp, writeBatch,
} from 'firebase/firestore'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { adminAuth, adminDb, adminStorage } from '../../../../packages/firebase/adminClient'
import { STORED_REPORT_STATUS } from '../../../../packages/types/admin'

const MAX_LIST = 500

function requireDb() {
  if (!adminDb) throw new Error('Firestore가 초기화되지 않았습니다.')
  return adminDb
}

export function toDate(value) {
  if (!value) return null
  if (typeof value.toDate === 'function') return value.toDate()
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatDateTime(value) {
  const date = toDate(value)
  if (!date) return '—'
  return new Intl.DateTimeFormat('ko-KR', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

function summary(value) {
  if (!value) return null
  const allowed = ['title', 'status', 'placement', 'actionType', 'priority', 'type', 'contentType', 'version', 'itemCount']
  return Object.fromEntries(allowed.filter(key => value[key] !== undefined).map(key => [key, value[key]]))
}

export function addAudit(batch, { action, entityType, entityId, actorUid, before, after, result = 'success' }) {
  const ref = doc(collection(requireDb(), 'adminAuditLogs'))
  batch.set(ref, {
    action, entityType, entityId, actorUid,
    beforeSummary: summary(before), afterSummary: summary(after),
    createdAt: serverTimestamp(), result,
  })
}

export { requireDb }

async function listCollection(name, options = {}) {
  const constraints = []
  if (options.orderBy) constraints.push(orderBy(options.orderBy, options.direction || 'desc'))
  constraints.push(limit(options.max || MAX_LIST))
  const snapshot = await getDocs(query(collection(requireDb(), name), ...constraints))
  return snapshot.docs.map(item => ({ id: item.id, ...item.data() }))
}

export async function fetchAnnouncements() {
  const items = await listCollection('inAppAnnouncements', { max: MAX_LIST })
  return items.sort((a, b) => (toDate(b.updatedAt)?.getTime() || 0) - (toDate(a.updatedAt)?.getTime() || 0))
}

export async function fetchAnnouncement(id) {
  const snapshot = await getDoc(doc(requireDb(), 'inAppAnnouncements', id))
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null
}

function announcementPayload(input, uid, { isCreate = false } = {}) {
  const startAt = input.startAt ? Timestamp.fromDate(new Date(input.startAt)) : null
  const endAt = input.endAt ? Timestamp.fromDate(new Date(input.endAt)) : null
  return {
    title: input.title.trim(), description: input.description.trim(), imageUrl: input.imageUrl?.trim() || null,
    status: input.status, placement: input.placement,
    actionLabel: input.actionType === 'dismiss' ? null : input.actionLabel.trim(), actionType: input.actionType,
    actionTarget: input.actionType === 'dismiss' ? null : input.actionTarget.trim(),
    startAt, endAt, minAppVersion: input.minAppVersion?.trim() || null, maxAppVersion: input.maxAppVersion?.trim() || null,
    targetPlatforms: input.targetPlatforms, displayRule: input.displayRule, priority: Number(input.priority),
    updatedAt: serverTimestamp(), updatedBy: uid,
    ...(isCreate ? { createdAt: serverTimestamp(), createdBy: uid, publishedAt: input.status === 'published' ? serverTimestamp() : null } : {}),
    ...(!isCreate && input.status === 'published' ? { publishedAt: input.publishedAt || serverTimestamp() } : {}),
  }
}

export async function saveAnnouncement(input, { uid, before = null, action = 'update' }) {
  const db = requireDb()
  const ref = input.id ? doc(db, 'inAppAnnouncements', input.id) : doc(collection(db, 'inAppAnnouncements'))
  const isCreate = !input.id
  const payload = announcementPayload(input, uid, { isCreate })
  const batch = writeBatch(db)
  batch.set(ref, payload, { merge: !isCreate })
  addAudit(batch, { action: isCreate ? 'create' : action, entityType: 'announcement', entityId: ref.id, actorUid: uid, before, after: input })
  await batch.commit()
  return ref.id
}

export async function duplicateAnnouncement(item, uid) {
  return saveAnnouncement({ ...item, id: null, title: `${item.title} 복사본`, status: 'draft', startAt: '', endAt: '', publishedAt: null }, { uid, before: null, action: 'duplicate' })
}

export async function removeAnnouncement(item, uid) {
  const db = requireDb()
  const batch = writeBatch(db)
  batch.delete(doc(db, 'inAppAnnouncements', item.id))
  addAudit(batch, { action: 'delete', entityType: 'announcement', entityId: item.id, actorUid: uid, before: item, after: null })
  await batch.commit()
}

export async function uploadAnnouncementImage(file, announcementId, uid) {
  if (!adminStorage) throw new Error('Firebase 공개 Storage 설정이 필요합니다.')
  if (!file?.type?.startsWith('image/')) throw new Error('이미지 파일만 업로드할 수 있습니다.')
  if (file.size > 5 * 1024 * 1024) throw new Error('이미지는 5MB 이하여야 합니다.')
  const extension = (file.name.split('.').pop() || 'jpg').replace(/[^a-zA-Z0-9]/g, '')
  const objectRef = ref(adminStorage, `admin-announcements/${announcementId || 'draft'}/${Date.now()}-${uid}.${extension}`)
  await uploadBytes(objectRef, file, { contentType: file.type, customMetadata: { uploadedBy: uid } })
  return getDownloadURL(objectRef)
}

export async function fetchReports() {
  const items = await listCollection('feedbackReports', { max: MAX_LIST })
  return items.sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0))
}

export async function updateReportStatus(report, nextStatus, uid) {
  const db = requireDb()
  const stored = STORED_REPORT_STATUS[nextStatus]
  if (!stored) throw new Error('올바르지 않은 제보 상태입니다.')
  const batch = writeBatch(db)
  const after = { ...report, status: stored }
  batch.update(doc(db, 'feedbackReports', report.id), { status: stored, updatedAt: serverTimestamp(), updatedBy: uid })
  addAudit(batch, { action: 'status-change', entityType: 'report', entityId: report.id, actorUid: uid, before: report, after })
  await batch.commit()
}

export async function saveReportReply(reportId, content) {
  const trimmed = content.trim()
  if (!trimmed) throw new Error('답변 내용을 입력해 주세요.')
  const token = await adminAuth?.currentUser?.getIdToken()
  if (!token) throw new Error('로그인이 필요합니다.')
  // 기존 replies API는 메시지 저장, unread 표시, idempotent outbox 푸시를 하나의 계약으로 처리한다.
  const response = await fetch('/api/reports/reply', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ reportId, content: trimmed }),
  })
  const data = await response.json().catch(() => null)
  if (!response.ok || !data?.ok) {
    console.error('[saveReportReply] failed', { status: response.status, code: data?.code ?? null })
    throw new Error(data?.code === 'rate_limited' ? '잠시 후 다시 시도해 주세요.' : '답변을 저장하지 못했어요.')
  }
}

export async function fetchReportNotes(reportId) {
  const snapshot = await getDocs(query(collection(requireDb(), 'feedbackReports', reportId, 'adminNotes'), orderBy('createdAt', 'desc'), limit(100)))
  return snapshot.docs.map(item => ({ id: item.id, ...item.data() }))
}

export async function addReportNote(reportId, content, uid) {
  const db = requireDb()
  const noteRef = doc(collection(db, 'feedbackReports', reportId, 'adminNotes'))
  const batch = writeBatch(db)
  batch.set(noteRef, { content: content.trim(), actorUid: uid, createdAt: serverTimestamp() })
  addAudit(batch, { action: 'note-create', entityType: 'report', entityId: reportId, actorUid: uid, before: null, after: { type: 'admin-note' } })
  await batch.commit()
  return noteRef.id
}

export async function fetchAuditLogs() {
  return listCollection('adminAuditLogs', { orderBy: 'createdAt', direction: 'desc', max: 300 })
}

export async function fetchNotificationSettings(uid) {
  const snapshot = await getDoc(doc(requireDb(), 'adminNotificationSettings', uid))
  return snapshot.exists() ? snapshot.data() : null
}

export async function saveNotificationSettings(uid, settings, before = null) {
  const db = requireDb()
  const batch = writeBatch(db)
  batch.set(doc(db, 'adminNotificationSettings', uid), { ...settings, actorUid: uid, updatedAt: serverTimestamp() }, { merge: true })
  addAudit(batch, { action: 'notification-settings-update', entityType: 'notification-settings', entityId: uid, actorUid: uid, before, after: settings })
  await batch.commit()
}

async function adminApiRequest(path, options = {}) {
  const token = await adminAuth?.currentUser?.getIdToken()
  if (!token) throw new Error('로그인이 필요합니다.')
  const response = await fetch(path, { ...options, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) } })
  const result = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(result.message || result.code || '요청에 실패했습니다.')
  return result
}

export async function fetchAdminTestDeviceStatus() {
  return adminApiRequest('/api/admin/notifications/test-devices')
}

export async function fetchPushCoverage() {
  return adminApiRequest('/api/admin/notifications/coverage')
}

export async function fetchScheduledPushes() {
  const result = await adminApiRequest('/api/admin/notifications/scheduled')
  return Array.isArray(result) ? result : []
}

export async function cancelScheduledPush(id) {
  return adminApiRequest(`/api/admin/notifications/scheduled?id=${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function fetchPushHistory() {
  const result = await adminApiRequest('/api/admin/notifications/history')
  return Array.isArray(result) ? result : []
}

export async function fetchDashboardData() {
  const [reports, announcements, events, operational, audits] = await Promise.all([
    fetchReports(), fetchAnnouncements(),
    listCollection('inAppAnnouncementEvents', { max: MAX_LIST }).catch(error => ({ error })),
    listCollection('adminOperationalEvents', { max: 50 }).catch(error => ({ error })),
    fetchAuditLogs(),
  ])
  const now = new Date()
  const sevenDaysAgo = new Date(now.getTime() - 7 * 86400000)
  const eventList = Array.isArray(events) ? events : []
  const recentEvents = eventList.filter(item => (toDate(item.createdAt)?.getTime() || 0) >= sevenDaysAgo.getTime())
  return {
    pendingReports: reports.filter(item => ['pending', 'new'].includes(item.status)).length,
    // "검토 대기"(pending/new, 아직 아무도 안 봄)와 구분되는 "미답변"(reviewing, 검토는
    // 시작했지만 아직 관리자 메모/해결 없이 남아있는 제보) — 이미 가져온 reports로 추가 읽기 없이 계산한다.
    reviewingReports: reports.filter(item => item.status === 'reviewing').length,
    publishedAnnouncements: announcements.filter(item => item.status === 'published').length,
    scheduledAnnouncements: announcements.filter(item => item.status === 'scheduled').length,
    impressions: Array.isArray(events) ? recentEvents.filter(item => item.type === 'impression').length : null,
    ctaClicks: Array.isArray(events) ? recentEvents.filter(item => item.type === 'ctaClick').length : null,
    recentOperationalErrors: Array.isArray(operational) ? operational.sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0)).slice(0, 8) : [],
    recentAudits: audits.slice(0, 8),
    recentDeployments: audits.filter(item => ['publish', 'rollback'].includes(item.action)).slice(0, 6),
    partialErrors: [!Array.isArray(events) ? '안내 지표' : null, !Array.isArray(operational) ? '운영 오류' : null].filter(Boolean),
  }
}
